import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

/**
 * Creates a proxy-specific axios instance with a custom base URL
 * @param baseURL - The base URL for the proxy (defaults to '/api/proxy')
 * @param apiKey - Optional API key to use for all requests (defaults to the default proxy key)
 * @param getAuthToken - Optional function to get the current user's JWT token
 * @returns An axios instance configured for the proxy
 */
const createProxyInstance = (
  baseURL: string = '/api/proxy', 
  apiKey: string = 'ak_default_proxy_key_12345',
  getAuthToken?: () => string | null
): AxiosInstance => {
  const instance = axios.create({
    baseURL, // This will be routed through your proxy middleware
    timeout: 10000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'X-ProxyApp-Api-Key': apiKey, // Include API key in all requests
    },
  });
  
  // Add request interceptor to ensure API key and auth token are included
  instance.interceptors.request.use(config => {
    // Make sure headers object exists
    config.headers = config.headers || {};
    
    // If X-ProxyApp-Api-Key header is not set, set it
    if (!config.headers['X-ProxyApp-Api-Key']) {
      config.headers['X-ProxyApp-Api-Key'] = apiKey;
    }
    return config;
  });
  
  return instance;
};

// Default instance with default base URL
const proxyAxiosInstance = createProxyInstance();

// Add response interceptor to standardize error handling
proxyAxiosInstance.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx causes this function to trigger
    return response;
  },
  (error) => {
    // Any status codes outside the range of 2xx cause this function to trigger
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Proxy response error:', error.response.data);
      
      // Check for authentication errors (401/403)
      if (error.response.status === 401 || error.response.status === 403) {
        console.warn('Authentication error detected, redirecting to login page');
        
        // Get the server error message to display to the user
        let errorMessage = 'Your session has expired. Please log in again.';
        
        // Try to extract the actual error message from the response
        if (error.response.data?.error) {
          if (typeof error.response.data.error === 'object') {
            errorMessage = error.response.data.error.message || errorMessage;
          } else {
            errorMessage = error.response.data.error;
          }
        }
        
        // Store the error message in sessionStorage so it can be displayed on the login page
        sessionStorage.setItem('auth_error_message', errorMessage);
        
        // Redirect to login page
        window.location.href = '/login?session_expired=true';
        
        // Return a special rejection that components can check for
        return Promise.reject({
          ...error,
          isAuthError: true,
          redirected: true,
          errorMessage
        });
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Proxy request error:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Proxy error:', error.message);
    }
    
    // Pass the error along to be handled by the component
    return Promise.reject(error);
  }
);

export default proxyAxiosInstance;
export { createProxyInstance };
